import React, { useCallback, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

interface Props {
  proximity: Array<{
    proximity: string
    selected: boolean
  }>
  onProximitySelect: (proximity: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripProximitySelection({
  proximity,
  onProximitySelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((proximityValue: string) => {
    onProximitySelect(proximityValue)
    onNext()
  }, [onProximitySelect, onNext])

  const tripProximityMap = useMemo(() =>
    arrayToMap(proximity, proximityOption => proximityOption.proximity, proximityOption => proximityOption.selected),
  [proximity])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'travel_distance',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const PROXIMITY = [
    {
      proximity: 'Keep it local',
      title: intl.formatMessage({
        id: '3MyZZYY8YBEnbnwiTCny',
        defaultMessage: 'Keep it local',
        description: 'Heading title for local proximity option in the trip planner setup wizard',
      }),
      description: intl.formatMessage({
        id: 'ehUDDjd2LUWkT2tX4RCg',
        defaultMessage: 'Low travel time, high relaxation.',
        description: 'Description for local proximity option in the trip planner setup wizard',
      }),
      icon: <LineWalkingIcon />,
    },
    {
      proximity: 'See the best within reach',
      title: intl.formatMessage({
        id: 'SuDquqvTCrqQ0dlOTJQ1',
        defaultMessage: 'Balanced',
        description: 'Heading title for balanced proximity option in the trip planner setup wizard',
      }),
      description: intl.formatMessage({
        id: '7UYr_0JhxQwFniVk7TrE',
        defaultMessage: 'The ideal mix of local and discovery.',
        description: 'Description for balanced proximity option in the trip planner setup wizard',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      proximity: 'Keen to get out and about',
      title: intl.formatMessage({
        id: 'o_5190vSkQgSXk11Rr_u',
        defaultMessage: 'Go all out',
        description: 'Heading title for adventurous proximity option in the trip planner setup wizard',
      }),
      description: intl.formatMessage({
        id: 'e99sSS1h3ljArKDyBEZm',
        defaultMessage: 'Stretch the map, make it count.',
        description: 'Description for adventurous proximity option on the map view',
      }),
      icon: <LineCompassIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'TzuILGZiUWp5zP7LDKsL',
      defaultMessage: 'Travel <i>Distance</i>',
      description: 'Heading text with italicized Distance in the trip planner setup wizard',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'Q_NQe9KvacuhhORMXNDF',
    defaultMessage: 'How far are you happy to venture from your hotel?',
    description: 'Question asking about preferred travel distance in the trip planner setup wizard',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>
      <TileCardsGroup>
        {PROXIMITY.map(proximityOption => (
          <TileCard
            key={proximityOption.proximity}
            title={proximityOption.title}
            subtitle={proximityOption.description}
            icon={proximityOption.icon}
            value={proximityOption.proximity}
            onClick={() => handleOnClick(proximityOption.proximity)}
            selected={!!tripProximityMap.get(proximityOption.proximity)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripProximitySelection
